class PartnersPatternWaves extends HTMLElement {
  #canvas;
  #context;
  #resizeObserver;
  #intersectionObserver;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #visible = false;
  #frame = 0;
  #lastFrame = 0;
  #started = 0;
  #point = null;

  connectedCallback() {
    this.#canvas = this.querySelector('canvas');
    this.#context = this.#canvas?.getContext('2d', { alpha: false });
    if (!this.#context) return;
    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(this);
    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.#start();
      else this.#stop();
    });
    this.#intersectionObserver.observe(this);
    this.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.addEventListener('pointerleave', this.#onPointerLeave);
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#motion.addEventListener('change', this.#onMotion);
  }

  disconnectedCallback() {
    this.#stop();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#motion.removeEventListener('change', this.#onMotion);
  }

  #onPointerMove = (event) => {
    if (event.pointerType === 'touch' || this.#motion.matches || this.dataset.interactive !== 'true') return;
    const bounds = this.getBoundingClientRect();
    this.#point = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    if (this.#motion.matches) this.#render();
  };
  #onPointerLeave = () => { this.#point = null; if (this.#motion.matches) this.#render(); };
  #onVisibility = () => { if (document.hidden) this.#stop(); else this.#start(); };
  #onMotion = () => { if (this.#motion.matches) { this.#point = null; this.#stop(); this.#render(); } else this.#start(); };

  #start() {
    if (!this.#visible || document.hidden || this.#frame) return;
    if (!this.#started) this.#started = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }
  #stop() { cancelAnimationFrame(this.#frame); this.#frame = 0; }
  #tick = (now) => {
    this.#frame = 0;
    if (!this.#visible || document.hidden) return;
    if (now - this.#lastFrame >= 33) { this.#lastFrame = now; this.#render(now); }
    if (!this.#motion.matches && this.dataset.paused !== 'true') this.#frame = requestAnimationFrame(this.#tick);
  };

  #render(now = performance.now()) {
    if (!this.#context || !this.#visible) return;
    const width = this.clientWidth, height = this.clientHeight;
    if (!width || !height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.round(width * dpr), pixelHeight = Math.round(height * dpr);
    if (this.#canvas.width !== pixelWidth || this.#canvas.height !== pixelHeight) {
      this.#canvas.width = pixelWidth;
      this.#canvas.height = pixelHeight;
    }
    const ctx = this.#context;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = this.dataset.backgroundColor || '#120f17';
    ctx.fillRect(0, 0, width, height);
    const spacing = Math.max(6, Number(this.dataset.spacing) || 9);
    const radius = Math.max(0.3, Number(this.dataset.markSize) || 0.95);
    const depth = Number(this.dataset.depth) || 0.95;
    const speed = Number(this.dataset.speed) || 0.35;
    const cursorSize = Number(this.dataset.cursorSize) || 50;
    const cursorStrength = Number(this.dataset.cursorStrength) || 0.6;
    const fadeSize = Number(this.dataset.fadeSize) || 0.5;
    const contrast = Number(this.dataset.contrast) || 1.2;
    const opacity = Number(this.dataset.opacity) || 1;
    const shine = Number(this.dataset.shine) || 0.8;
    const angle = (Number(this.dataset.direction) || 20) * Math.PI / 180;
    const waveTime = this.#motion.matches ? 0 : (now - this.#started) * 0.001 * speed;
    const intro = this.dataset.intro === 'true' && !this.#motion.matches ? Math.min(1, (now - this.#started) / 900) : 1;
    const centerX = width / 2, centerY = height / 2;
    const maxX = Math.max(1, centerX), maxY = Math.max(1, centerY);
    const color = this.dataset.color || '#ffffff';
    ctx.fillStyle = color;
    for (let y = spacing / 2; y < height; y += spacing) {
      for (let x = spacing / 2; x < width; x += spacing) {
        const diagonal = (x * Math.cos(angle) + y * Math.sin(angle)) / spacing;
        const silk = Math.sin(diagonal * 0.105 + waveTime) * Math.cos(y * 0.013 - waveTime * 0.7);
        let drawX = x + Math.sin(y * 0.012 + waveTime) * 7 * depth;
        let drawY = y + silk * 12 * depth;
        if (this.#point) {
          const dx = drawX - this.#point.x, dy = drawY - this.#point.y;
          const distance = Math.hypot(dx, dy);
          if (distance < cursorSize) {
            const push = (1 - distance / cursorSize) ** 2 * cursorSize * cursorStrength;
            drawX += dx / Math.max(1, distance) * push;
            drawY += dy / Math.max(1, distance) * push;
          }
        }
        const edge = Math.min(1, (1 - Math.abs(drawX - centerX) / maxX) / fadeSize, (1 - Math.abs(drawY - centerY) / maxY) / fadeSize);
        const alpha = Math.max(0, Math.min(1, edge)) * Math.max(0.12, 0.48 + silk * 0.25 * shine) * contrast * opacity * intro;
        if (alpha <= 0.01) continue;
        ctx.globalAlpha = Math.min(1, alpha);
        ctx.beginPath();
        ctx.arc(drawX, drawY, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }
}

if (!customElements.get('partners-pattern-waves')) customElements.define('partners-pattern-waves', PartnersPatternWaves);
